import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

/**
 * The studio side of casting: creating calls and reviewing applications.
 *
 * These endpoints already existed and had no screen, which is why production has no
 * casting calls, no members and no productions - the whole community side was reachable
 * only by calling the API directly.
 */

export interface CastingCall {
  id: string;
  slug: string;
  title: string;
  callType: string;
  callTypeLabel?: string;
  department?: string | null;
  departmentLabel?: string | null;
  roleName?: string | null;
  roleSummary?: string | null;
  location?: string | null;
  remoteFriendly: boolean;
  paid: boolean;
  compensationSummary?: string | null;
  shootDatesSummary?: string | null;
  ageRange?: string | null;
  submissionDeadline?: string | null;
  status: string;
  acceptingApplications?: boolean;
}

export interface CastingCallInput {
  title: string;
  callType: string;
  roleName?: string;
  roleSummary?: string;
  description?: string;
  requirements?: string;
  location?: string;
  ageRange?: string;
  remoteFriendly?: boolean;
  paid?: boolean;
  compensationSummary?: string;
  shootDatesSummary?: string;
  submissionDeadline?: string;
  status?: string;
  openings?: number;
}

export interface CastingApplication {
  id: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone?: string | null;
  location?: string | null;
  coverNote?: string | null;
  reelUrl?: string | null;
  selfTapeUrl?: string | null;
  portfolioUrl?: string | null;
  status: string;
  statusLabel?: string | null;
  studioNotes?: string | null;
  submittedAt?: string | null;
  memberSlug?: string | null;
  memberAvatarUrl?: string | null;
  memberCraftLabel?: string | null;
}

/** Decisions that end the applicant's involvement, and so require a note. Mirrors the server. */
export const DECISIONS_NEEDING_A_NOTE = ['CAST', 'NOT_PROGRESSING'];

export const APPLICATION_STATUSES: { value: string; label: string; help: string }[] = [
  { value: 'UNDER_REVIEW', label: 'Under review', help: 'Internal only - the applicant is not emailed.' },
  { value: 'SHORTLISTED', label: 'Shortlisted', help: 'The applicant is emailed.' },
  { value: 'AUDITION_INVITED', label: 'Invite to audition', help: 'The applicant is emailed.' },
  { value: 'CAST', label: 'Cast', help: 'The applicant is emailed. A note is required.' },
  { value: 'NOT_PROGRESSING', label: 'Not progressing', help: 'The applicant is emailed. A note is required.' },
];

function apiUrl(path: string): string {
  const base = (config.apiBaseUrl || '').replace(/\/+$/, '');
  return base.endsWith('/api') ? `${base}/casting${path}` : `${base}/api/casting${path}`;
}

/** Surfaces the server's own message, which carries the reason a decision was refused. */
async function unwrap<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.message || `Request failed (${response.status})`);
  }
  return (json?.data ?? json) as T;
}

function listOf<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const page = payload as { content?: T[] } | null;
  return page?.content ?? [];
}

export async function listCalls(status?: string): Promise<CastingCall[]> {
  const query = status ? `?status=${encodeURIComponent(status)}&size=100` : '?size=100';
  const response = await unifiedAuth.fetch(apiUrl(`/studio/calls${query}`), { method: 'GET' });
  return listOf<CastingCall>(await unwrap(response));
}

export async function createCall(input: CastingCallInput): Promise<CastingCall> {
  const response = await unifiedAuth.fetch(apiUrl('/studio/calls'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return unwrap<CastingCall>(response);
}

export async function updateCall(slug: string, input: CastingCallInput): Promise<CastingCall> {
  const response = await unifiedAuth.fetch(apiUrl(`/studio/calls/${encodeURIComponent(slug)}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return unwrap<CastingCall>(response);
}

export async function listApplications(slug: string): Promise<CastingApplication[]> {
  const response = await unifiedAuth.fetch(
    apiUrl(`/studio/calls/${encodeURIComponent(slug)}/applications?size=100`), { method: 'GET' });
  return listOf<CastingApplication>(await unwrap(response));
}

export async function decideApplication(
  applicationId: string,
  status: string,
  studioNotes: string,
): Promise<CastingApplication> {
  const response = await unifiedAuth.fetch(apiUrl(`/studio/applications/${encodeURIComponent(applicationId)}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, studioNotes }),
  });
  return unwrap<CastingApplication>(response);
}
